/**
 * KeepAliveTabPanels — persistent tab mount for deal detail routes.
 *
 * Problem: heavy deal tabs (Agreement ~995 lines / 32 hooks, Details,
 * Review) take ~200-500ms to mount from scratch. React Router unmounts
 * the previous tab and mounts the next on every switch, so users pay
 * that cost repeatedly. Even with startTransition to eliminate the
 * empty flash, the actual mount cost remains and switches feel laggy.
 *
 * Fix: render every tab component that's been VISITED at least once,
 * hiding inactive ones with CSS (`display: none`). The active tab is
 * always the only one visible. Once a tab is visited, subsequent
 * switches back to it are instant — the component is already mounted
 * and its internal state is preserved.
 *
 * Trade-offs:
 * - Memory: N tab components stay mounted per deal-page visit (up
 *   from 1). For deal pages that's a few extra hundred KB of React
 *   fiber tree, negligible in practice.
 * - Persistent state: form drafts, edit mode, selected rows survive
 *   tab switches. Usually the desired behavior on a deal page — users
 *   bounce between Agreement and Details/Review while editing and
 *   want their work-in-progress preserved.
 *
 * Lazy mount: tabs that have never been visited render `null`, so
 * page-load cost is still just the initial tab. The first visit to
 * each other tab mounts it once; subsequent visits are cache hits.
 *
 * Route matching: react-router still owns the URL. In ProtectedApp,
 * KeepAlive'd tabs have `element={null}` so the Outlet renders nothing;
 * this component reads the wildcard param from the parent route and
 * decides which tab to show.
 */

import { Box } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

export interface KeepAlivePanel {
  /** Tab path segment (matches the react-router `path=` in ProtectedApp) */
  path: string
  /** Component to render for this tab. Lazily imported at module top-level. */
  Component: React.ComponentType
}

interface KeepAliveTabPanelsProps {
  panels: KeepAlivePanel[]
  /** Fallback for URLs that don't match any keep-alive'd panel — usually
   *  the parent's <Outlet /> so react-router can render dynamic routes
   *  (e.g., `strategy/:campaignId`) or placeholder tabs unchanged. */
  fallback?: React.ReactNode
}

/**
 * Extract the current tab segment from the parent route's wildcard.
 *
 * DealRouting matches `/deals/:PK/:SK/*` — the wildcard captures
 * anything after the SK, e.g. "agreement" or "strategy/foo". We only
 * care about the first segment for tab identity.
 */
function useCurrentTabPath(): string {
  const params = useParams()
  const wildcard = params['*'] ?? ''
  return wildcard.split('/')[0] ?? ''
}

export function KeepAliveTabPanels({ panels, fallback }: KeepAliveTabPanelsProps) {
  const currentPath = useCurrentTabPath()

  // Track which tab paths have been visited at least once. Once visited,
  // that path's component stays mounted for the lifetime of DealRouting.
  const [visited, setVisited] = useState<Set<string>>(() => {
    // Seed with the initial tab so the first render mounts it inline
    // rather than waiting for the effect below.
    return new Set(currentPath ? [currentPath] : [])
  })

  useEffect(() => {
    if (!currentPath) return
    setVisited(prev => {
      if (prev.has(currentPath)) return prev
      const next = new Set(prev)
      next.add(currentPath)
      return next
    })
  }, [currentPath])

  const panelMap = useMemo(() => {
    const m = new Map<string, React.ComponentType>()
    for (const p of panels) m.set(p.path, p.Component)
    return m
  }, [panels])

  const isKeepAlivePath = panelMap.has(currentPath)

  return (
    <>
      {panels.map(({ path, Component }) => {
        if (!visited.has(path)) return null
        const isActive = path === currentPath
        return (
          // Use style rather than the `hidden` attribute — `hidden`
          // triggers additional accessibility signaling we don't need,
          // and some MUI components measure inline dimensions on mount
          // which behave more predictably with display:none.
          <Box
            key={path}
            sx={{
              display: isActive ? 'block' : 'none',
              // Keep the mounted-but-hidden trees out of the tab order
              // and screen-reader tree so users only interact with the
              // active tab.
              ...(isActive
                ? {}
                : {
                    // aria-hidden alone is easy to circumvent via focus;
                    // pointer-events + visibility:hidden prevent stray
                    // interactions while the tab is offscreen.
                    pointerEvents: 'none',
                  }),
            }}
            aria-hidden={!isActive}
          >
            <Component />
          </Box>
        )
      })}
      {/* Non-keep-alive routes fall through to the parent's Outlet
       *  (strategy/:campaignId, tasks, services, etc.) */}
      {!isKeepAlivePath && fallback}
    </>
  )
}

export default KeepAliveTabPanels
